import { lazy, type ComponentType } from "react";
import { createBrowserRouter, createHashRouter, Navigate, type RouteObject } from "react-router-dom";
import { AppLayout } from "../components/shell/AppLayout";
import type { RouteHandle } from "../components/shell/page-meta";
import { RequireAuth, RequireCapability } from "../lib/auth";
import { DashboardPage } from "../pages/DashboardPage";
import { LoginPage } from "../pages/LoginPage";
import { NotFoundPage } from "../pages/NotFoundPage";

/** Code-split everything except the first screens people land on. */
function page<K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => loader().then((m) => ({ default: m[name] })));
}

const ScheduleLayout = page(() => import("../pages/schedule/ScheduleLayout"), "ScheduleLayout");
const CalendarTab = page(() => import("../pages/schedule/CalendarTab"), "CalendarTab");
const ListTab = page(() => import("../pages/schedule/ListTab"), "ListTab");
const RotationTab = page(() => import("../pages/schedule/RotationTab"), "RotationTab");
const SummaryListPage = page(() => import("../pages/summary/SummaryListPage"), "SummaryListPage");
const SummaryDetailPage = page(() => import("../pages/summary/SummaryDetailPage"), "SummaryDetailPage");
const SummaryEditPage = page(() => import("../pages/summary/SummaryEditPage"), "SummaryEditPage");
const InsightsPage = page(() => import("../pages/InsightsPage"), "InsightsPage");
const ActivityPage = page(() => import("../pages/ActivityPage"), "ActivityPage");
const MySchedulePage = page(() => import("../pages/MySchedulePage"), "MySchedulePage");
const NotificationsPage = page(() => import("../pages/NotificationsPage"), "NotificationsPage");
const ProfilePage = page(() => import("../pages/ProfilePage"), "ProfilePage");
const AdminLayout = page(() => import("../pages/admin/AdminLayout"), "AdminLayout");
const MembersAdmin = page(() => import("../pages/admin/MembersAdmin"), "MembersAdmin");
const CategoriesAdmin = page(() => import("../pages/admin/CategoriesAdmin"), "CategoriesAdmin");
const RotationAdmin = page(() => import("../pages/admin/RotationAdmin"), "RotationAdmin");
const SettingsAdmin = page(() => import("../pages/admin/SettingsAdmin"), "SettingsAdmin");

const h = (handle: RouteHandle) => handle;
const summaryParent = { label: "Summary", to: "/summary" };

export const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage />, handle: h({ title: "Log in" }) },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "dashboard", element: <DashboardPage />, handle: h({ title: "Dashboard", primary: "new-schedule" }) },
      {
        path: "schedule",
        element: <ScheduleLayout />,
        handle: h({ title: "Schedule", primary: "new-schedule" }),
        children: [
          { index: true, element: <Navigate to="calendar" replace /> },
          { path: "calendar", element: <CalendarTab /> },
          { path: "list", element: <ListTab /> },
          { path: "rotation", element: <RotationTab /> },
        ],
      },
      {
        path: "summary",
        children: [
          { index: true, element: <SummaryListPage />, handle: h({ title: "Summary" }) },
          { path: ":sessionId", element: <SummaryDetailPage />, handle: h({ title: "Summary", parent: summaryParent }) },
          {
            path: ":sessionId/edit",
            element: (
              <RequireCapability cap="summary.edit">
                <SummaryEditPage />
              </RequireCapability>
            ),
            handle: h({ title: "Write summary", parent: summaryParent }),
          },
        ],
      },
      { path: "insights", element: <InsightsPage />, handle: h({ title: "Topic Insights" }) },
      {
        path: "activity",
        element: (
          <RequireCapability cap="activity.view">
            <ActivityPage />
          </RequireCapability>
        ),
        handle: h({ title: "Activity Log" }),
      },
      {
        path: "my-schedule",
        element: (
          <RequireCapability cap="mySchedule.view">
            <MySchedulePage />
          </RequireCapability>
        ),
        handle: h({ title: "My Schedule" }),
      },
      { path: "notifications", element: <NotificationsPage />, handle: h({ title: "Notifications" }) },
      { path: "profile", element: <ProfilePage />, handle: h({ title: "Profile" }) },
      {
        path: "admin",
        element: (
          <RequireCapability cap="admin.access">
            <AdminLayout />
          </RequireCapability>
        ),
        handle: h({ title: "Manage System" }),
        children: [
          { index: true, element: <Navigate to="members" replace /> },
          { path: "members", element: <MembersAdmin /> },
          { path: "categories", element: <CategoriesAdmin /> },
          { path: "rotation", element: <RotationAdmin /> },
          { path: "settings", element: <SettingsAdmin /> },
        ],
      },
      { path: "*", element: <NotFoundPage />, handle: h({ title: "Page not found" }) },
    ],
  },
];

const basename = import.meta.env.BASE_URL.replace(/\/$/, "") || undefined;

export const router = import.meta.env.VITE_ROUTER === "hash" ? createHashRouter(routes) : createBrowserRouter(routes, { basename });
